import React, { useEffect, useState, useRef } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import createComponent from 'styles/fela/createComponent';
import { trapFocus } from 'utils/focus';

let isMouseInteraction = false;
let listenersRegistered = false;

if (typeof window !== 'undefined' && !listenersRegistered) {
  listenersRegistered = true;
  window.addEventListener('mousedown', () => {
    isMouseInteraction = true;
  });
  window.addEventListener('touchstart', () => {
    isMouseInteraction = true;
  });
  window.addEventListener('keydown', () => {
    isMouseInteraction = false;
  });
}

const Container = createComponent(({ theme, hide }) => ({
  position: 'fixed',
  bottom: '0',
  right: '0',
  left: '0',
  top: '0',
  zIndex: theme.zIndex.modal,

  display: 'flex',
  alignItems: 'center',
  flexDirection: 'column',

  background: theme.color.overlayModal,
  userSelect: 'none', // without this click on container selects text in modal
  transition: 'opacity .3s',
  overflow: 'auto',

  extend: [
    {
      condition: hide,
      style: {
        opacity: 0,
        pointerEvents: 'none',
        visibility: 'hidden',
      },
    },
  ],
}), 'div', ['hide']);

const ModalWrapper = createComponent(() => ({
  userSelect: 'initial', // so now we can select text in modal
  display: 'flex',
  justifyContent: 'center',
  marginTop: 'auto',
  marginBottom: 'auto',
  position: 'relative',
}));

export default function Modal({
  className = undefined,
  children,
  onDismiss,
  hide = false,
  autoFocusFirstElement = true,
  ...props
}) {
  const [didMount, setDidMount] = useState(false);
  const containerRef = useRef();
  const wrapperRef = useRef();
  const previousFocus = useRef(null);

  // We need to attempt focus multiple times or wait for children to be stable
  // Using a callback ref for wrapper would be ideal, but it's a generated component.
  // We'll stick to an effect but use requestAnimationFrame for better DOM timing.

  const onDismissHandler = (e) => {
    if (e.target === wrapperRef.current || e.target === containerRef.current) {
      onDismiss?.();
    }
  };

  useEffect(() => {
    if (hide) return undefined;

    previousFocus.current = document.activeElement;

    const onEscapeKey = (e) => {
      if (onDismiss && e.key === 'Escape') {
        onDismiss();
      }
    };

    setDidMount(true);
    document.addEventListener('keydown', onEscapeKey);

    return () => {
      document.removeEventListener('keydown', onEscapeKey);
      // Restore focus
      if (previousFocus.current) {
        if (isMouseInteraction) {
          previousFocus.current.focus({ preventScroll: true, focusVisible: false });
          previousFocus.current.blur();
        } else {
          previousFocus.current.focus();
        }
      }
    };
  }, [hide, onDismiss]);

  useEffect(() => {
    if (hide || !didMount) return;

    const cleanupTrapFocus = trapFocus(wrapperRef.current);

    // Use requestAnimationFrame to ensure the browser has painted
    // and the elements are focusable.
    let timeoutId;
    const animationFrame = requestAnimationFrame(() => {
      // Use setTimeout as a fallback to ensure React has fully committed the component tree
      timeoutId = setTimeout(() => {
        const focusableSelector = 'input:not([type="hidden"]), a[href], button:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])';
        if (!autoFocusFirstElement) {
          containerRef.current?.focus();
          return;
        }

        if (wrapperRef.current) {
          const focusable = wrapperRef.current.querySelectorAll(focusableSelector);
          if (focusable && focusable.length) {
            focusable[0].focus();
          } else if (containerRef.current) {
            containerRef.current.focus();
          }
        }
      }, 50);
    });

    return () => {
      cleanupTrapFocus();
      cancelAnimationFrame(animationFrame);
      clearTimeout(timeoutId);
    };
  }, [didMount, autoFocusFirstElement, hide]);

  // make sure a DOM element for the portal already exists
  if (!didMount) {
    return null;
  }

  return ReactDOM.createPortal(
    <Container
      className={className}
      onClick={onDismissHandler}
      innerRef={containerRef}
      hide={hide}
      role="dialog"
      aria-modal="true"
      tabIndex="-1"
      {...props}
    >
      <ModalWrapper innerRef={wrapperRef}>
        {children}
      </ModalWrapper>
    </Container>,
    document.getElementById('modal'),
  );
}

Modal.propTypes = {
  className: PropTypes.string,
  children: PropTypes.node.isRequired,
  onDismiss: PropTypes.func,
  hide: PropTypes.bool,
  autoFocusFirstElement: PropTypes.bool,
};
